import config from 'constants/config'
import { ANYWHERE_NAME, ANYWHERE_PLACE_ID } from 'constants/search'
import { getDestinationImage } from 'lib/search/searchUtils'
import { getImageUrl } from 'lib/image/imageUtils'
import { defineMessages, type IntlShape } from 'react-intl'

type SearchItemLike = App.SearchItem | undefined

interface Params {
  intl: IntlShape
  searchItem: SearchItemLike
  destinationId?: string
  destinationName?: string
  totalCount: number
  specificPlaces?: Array<App.SpecificPlace> | undefined
  destinationImageId?: string
}

interface Result {
  title: string
  description: string
  imageId?: string
  imageUrl?: string
  imageAlt: string
}

const messages = defineMessages({
  propertyDescriptionWithLocation: {
    id: 'pdr12ZmwV3BFF5AndsvW',
    defaultMessage: 'Discover {propertyName} in {propertyLocation} with {brandName}.',
    description: 'SEO meta description for a property search result page when the property location is available',
  },
  propertyDescriptionWithoutLocation: {
    id: 'DqvsbNdxRR9qgXfc5j5r',
    defaultMessage: 'Discover {propertyName} with {brandName}.',
    description: 'SEO meta description for a property search result page when only the property name is available',
  },
  anywhereTitle: {
    id: 'qPGDE3JLb4Ez_B27wlEr',
    defaultMessage: 'Browse the best hotels on {brandName}',
    description: 'SEO page title for accommodation search results when searching anywhere',
  },
  destinationTitle: {
    id: 'NWA1W56rG88L2k5-vZNQ',
    defaultMessage: 'Browse the best hotels in {locationName}',
    description: 'SEO page title for accommodation search results with a specific destination or location',
  },
  anywhereDescription: {
    id: '1IkUbhNSwgV9Fa6H12lo',
    defaultMessage: 'Explore hotels around the world with {brandName}.',
    description: 'SEO meta description for accommodation search results when searching anywhere',
  },
  destinationDescription: {
    id: 'rExKSGrJzFDfuvvRuy18',
    defaultMessage: 'Explore hotels in {locationName} with {brandName}.',
    description: 'SEO meta description for accommodation search results with a specific destination or location',
  },
  anywhereImageAlt: {
    id: 'H1dTQhLjO06zye41sqHz',
    defaultMessage: '{brandName} hotel search results',
    description: 'Alt text for the SEO image on accommodation search results when searching anywhere',
  },
  destinationImageAlt: {
    id: 'DfU79u1tqDiHLopcc__-',
    defaultMessage: '{locationName} hotels',
    description: 'Alt text for the SEO image on accommodation search results with a specific destination or location',
  },
})

function normaliseSecondaryText(secondaryText?: string): string | undefined {
  if (!secondaryText) {
    return undefined
  }
  return secondaryText
    .split('·')
    .map(part => part.trim())
    .filter(Boolean)
    .join(', ')
}

function findSpecificPlaceImage(places?: Array<App.SpecificPlace>): { imageId?: string, imageUrl?: string } {
  if (!places?.length) {
    return {}
  }

  const candidate = places.find(place => !!place.image)?.image
  if (!candidate) {
    return {}
  }

  if (candidate.startsWith('http')) {
    return { imageUrl: candidate }
  }

  return { imageId: candidate }
}

function isSearchDestination(searchItem: SearchItemLike): searchItem is App.SearchDestination {
  return searchItem?.searchType === 'destination'
}

function isSearchProperty(searchItem: SearchItemLike): searchItem is App.SearchProperty {
  return searchItem?.searchType === 'property'
}

export function getAccommodationSearchHeadMeta({
  intl,
  searchItem,
  destinationId,
  destinationName,
  specificPlaces,
  destinationImageId,
}: Params): Result {
  const searchDestination = isSearchDestination(searchItem) ? searchItem : undefined
  const isPropertySearch = isSearchProperty(searchItem)

  const mainText = searchItem?.format?.mainText?.trim()
  const secondaryText = normaliseSecondaryText(searchItem?.format?.secondaryText?.trim())

  const isAnywhereSearch = (destinationId && destinationId === ANYWHERE_PLACE_ID) ||
    (mainText && mainText.toLowerCase() === ANYWHERE_NAME.toLowerCase())

  const destinationType = searchDestination?.destinationType

  // Get imageId from searchItem, handling V3 structure
  const searchItemImageId = searchItem?.format?.imageId ||
    (searchItem?.searchType === 'searchItemV3' ? searchItem.display.imageId : searchItem?.imageId)
  const mappedDestinationImageId = destinationId ? getDestinationImage(destinationId) : undefined
  const specificPlaceImage = findSpecificPlaceImage(specificPlaces)

  if (isPropertySearch) {
    const propertyName = mainText || destinationName || config.title
    const propertyLocation = secondaryText || destinationName
    const propertyDescription = propertyLocation
      ? intl.formatMessage(
          messages.propertyDescriptionWithLocation,
          { propertyName, propertyLocation, brandName: config.title },
        )
      : intl.formatMessage(
          messages.propertyDescriptionWithoutLocation,
          { propertyName, brandName: config.title },
        )

    const imageId = searchItemImageId || destinationImageId || specificPlaceImage.imageId || mappedDestinationImageId
    let imageUrl = specificPlaceImage.imageUrl

    if (!imageUrl && imageId) {
      imageUrl = getImageUrl(imageId, OG_IMAGE_OPTIONS)
    }

    const fallbackImageId = imageId ?? config.head.brandOpenGraphImageId
    const fallbackImageUrl = imageUrl ?? getImageUrl(config.head.brandOpenGraphImageId, OG_IMAGE_OPTIONS)

    return {
      title: propertyName,
      description: propertyDescription,
      imageId: fallbackImageId,
      imageUrl: fallbackImageUrl,
      imageAlt: propertyName,
    }
  }

  const locationLabel = mainText || destinationName || config.title
  const descriptiveLocation = destinationType === 'country' || !secondaryText || secondaryText.localeCompare(locationLabel, undefined, { sensitivity: 'base' }) === 0
    ? locationLabel
    : `${locationLabel}, ${secondaryText}`
  const description = isAnywhereSearch
    ? intl.formatMessage(messages.anywhereDescription, { brandName: config.title })
    : intl.formatMessage(messages.destinationDescription, {
        locationName: descriptiveLocation,
        brandName: config.title,
      })

  const imageId = searchItemImageId || destinationImageId || specificPlaceImage.imageId || mappedDestinationImageId
  let imageUrl = specificPlaceImage.imageUrl

  if (!imageUrl && imageId) {
    imageUrl = getImageUrl(imageId, OG_IMAGE_OPTIONS)
  }

  const imageAlt = isAnywhereSearch
    ? intl.formatMessage(messages.anywhereImageAlt, { brandName: config.title })
    : intl.formatMessage(messages.destinationImageAlt, { locationName: descriptiveLocation })

  return {
    title: isAnywhereSearch
      ? intl.formatMessage(messages.anywhereTitle, { brandName: config.title })
      : intl.formatMessage(messages.destinationTitle, { locationName: descriptiveLocation }),
    description,
    imageId: imageId ?? config.head.brandOpenGraphImageId,
    imageUrl: imageUrl ?? getImageUrl(config.head.brandOpenGraphImageId, OG_IMAGE_OPTIONS),
    imageAlt,
  }
}
const OG_IMAGE_OPTIONS = { width: 1200, aspectRatio: '16:9', format: 'jpeg' as const }
